import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, Megaphone, Printer, Building2, Globe } from "lucide-react";
import { useContent } from "@/context/ContentContext";

const ICONS = {
  "reklam-tabela": Megaphone,
  "baski-matbaa": Printer,
  "yapi-ic-mekan": Building2,
  "dijital-medya": Globe,
};

export default function Services({ onQuote }) {
  const content = useContent();
  const sec = content.services;
  const categories = sec.categories;
  const [activeId, setActiveId] = useState(categories[0].id);
  const active = categories.find((c) => c.id === activeId) || categories[0];

  return (
    <section id="hizmetler" data-testid="services-section" className="relative bg-obsidian-800 py-24 sm:py-32 lg:py-40">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.7 }}
              className="font-mono text-xs uppercase tracking-[0.3em] text-gold"
            >
              {sec.eyebrow}
            </motion.p>
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.8, delay: 0.1 }}
              className="mt-6 font-display text-3xl font-bold tracking-tight text-zinc-100 sm:text-4xl lg:text-5xl"
            >
              {sec.titlePre} <span className="text-gold">{sec.titleGold}</span>
            </motion.h2>
          </div>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="max-w-md text-sm leading-relaxed text-zinc-400 sm:text-base"
          >
            {sec.intro}
          </motion.p>
        </div>

        <div className="mt-14 grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
          {categories.map((cat, i) => {
            const Icon = ICONS[cat.id] || Megaphone;
            const isActive = cat.id === activeId;
            return (
              <motion.button
                key={cat.id}
                data-testid={`category-tab-${cat.id}`}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.7, delay: 0.07 * i }}
                onClick={() => setActiveId(cat.id)}
                className={`group relative overflow-hidden rounded-2xl border p-5 text-left transition-colors duration-500 sm:p-6 ${
                  isActive
                    ? "border-gold/60 bg-gold/10 gold-glow"
                    : "border-gold/15 bg-obsidian hover:border-gold/40"
                }`}
              >
                <span className={`font-mono text-xs ${isActive ? "text-gold" : "text-zinc-500"}`}>{cat.number}</span>
                <Icon className={`mt-6 h-7 w-7 transition-colors duration-500 sm:mt-10 ${isActive ? "text-gold" : "text-zinc-400 group-hover:text-gold"}`} />
                <h3 className="mt-3 font-display text-base font-bold text-zinc-100 sm:text-xl">{cat.title}</h3>
                <p className="mt-1 hidden text-xs leading-relaxed text-zinc-500 sm:block">{cat.tagline}</p>
                {isActive && (
                  <motion.span
                    layoutId="cat-underline"
                    className="absolute bottom-0 left-0 h-0.5 w-full bg-gold"
                  />
                )}
              </motion.button>
            );
          })}
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={active.id}
            data-testid={`service-grid-${active.id}`}
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -16 }}
            transition={{ duration: 0.5, ease: [0.33, 1, 0.68, 1] }}
            className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3"
          >
            {active.services.map((s, i) => (
              <motion.div
                key={s.name}
                data-testid={`service-card-${s.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.55, delay: 0.06 * i, ease: [0.33, 1, 0.68, 1] }}
                className="group flex flex-col justify-between rounded-2xl border border-gold/15 bg-obsidian p-6 transition-colors duration-500 hover:border-gold/50 sm:p-7"
              >
                <div>
                  <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold/70">
                    {active.number}.{String(i + 1).padStart(2, "0")}
                  </span>
                  <h4 className="mt-3 font-display text-lg font-bold text-zinc-100 sm:text-xl">{s.name}</h4>
                  <p className="mt-2 text-sm leading-relaxed text-zinc-400">{s.desc}</p>
                </div>
                <button
                  data-testid={`service-quote-btn-${s.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  onClick={() => onQuote(s.name)}
                  className="mt-6 flex w-fit items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-gold transition-transform duration-300 hover:translate-x-1"
                >
                  Teklif Al
                  <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:rotate-45" />
                </button>
              </motion.div>
            ))}
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
}
